import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Send, MessageCircle, Download } from "lucide-react";
import { FadeIn, Eyebrow, EASE } from "./Reveal";

const CTA_IMAGE =
  "https://images.unsplash.com/flagged/photo-1574097656146-0b43b7660cb6?q=80&w=900&h=1150&auto=format&fit=crop";

const BUTTONS = [
  {
    label: "Join Our Telegram Channel",
    href: "https://t.me/edualertpartner",
    icon: Send,
    className: "bg-[#0088cc] hover:bg-[#0077b3] shadow-[#0088cc]/30",
    testid: "cta-telegram-button",
  },
  {
    label: "Join Our WhatsApp Channel",
    href: "https://whatsapp.com/channel/0029VbD2TVSAjPXOMi5Mpn2f",
    icon: MessageCircle,
    className: "bg-[#1faa53] hover:bg-[#189348] shadow-[#25d366]/20",
    testid: "cta-whatsapp-button",
  },
  {
    label: "Install EduAlert App",
    href: "https://play.google.com/store/apps/details?id=com.edualert.app",
    icon: Download,
    className: "bg-[#f57c00] hover:bg-[#e65100] shadow-[#f57c00]/40",
    testid: "cta-install-app-button",
  },
];

export default function CTAStackSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [-24, 24]);

  return (
    <section
      id="cta"
      ref={ref}
      data-testid="cta-section"
      className="grain relative overflow-hidden bg-[#1a237e] py-20 text-white sm:py-28"
    >
      <div className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-[#283593]/70 blur-3xl" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeIn>
              <Eyebrow index="04" label="Get Started" light />
              <h2 className="font-display mt-5 text-3xl font-black leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                Ready to Make an Impact &amp; Earn?
              </h2>
            </FadeIn>

            <div className="mt-10 grid max-w-xl gap-4">
              {BUTTONS.map((btn, i) => {
                const Icon = btn.icon;
                return (
                  <FadeIn key={btn.label} delay={i * 0.08}>
                    <motion.a
                      href={btn.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={btn.testid}
                      className={`btn-lift group flex items-center justify-between gap-4 rounded-2xl px-7 py-5 text-base font-bold shadow-xl sm:text-lg ${btn.className}`}
                      whileHover={{ scale: 1.015 }}
                      transition={{ duration: 0.3, ease: EASE }}
                    >
                      <span className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        {btn.label}
                      </span>
                      <span className="font-mono-edu text-sm opacity-60 transition-transform duration-300 group-hover:translate-x-1">
                        →
                      </span>
                    </motion.a>
                  </FadeIn>
                );
              })}
            </div>

            <FadeIn delay={0.3}>
              <p
                data-testid="cta-tagline"
                className="mt-8 max-w-md text-sm leading-relaxed text-white/70 sm:text-base"
              >
                Install the app. Get your referral code. Start sharing. Start
                earning!
              </p>
            </FadeIn>
          </div>

          <FadeIn className="lg:col-span-5" delay={0.15}>
            <div className="relative mx-auto max-w-sm lg:max-w-none">
              <div className="absolute -inset-0 translate-x-4 translate-y-4 rounded-[2rem] border-2 border-[#f57c00]" />
              <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
                <motion.img
                  src={CTA_IMAGE}
                  alt="Student reading an update"
                  data-testid="cta-image"
                  style={{ y: imgY, scale: 1.15 }}
                  className="h-[380px] w-full object-cover sm:h-[460px]"
                />
                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#0d1b60]/80 to-transparent" />
                <p className="absolute bottom-4 left-5 right-5 text-sm font-semibold text-white">
                  Never Miss an Important Update
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}